import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ClockStopwatchIcon, Tooltip, IconButton } from "@activecollab/components";
import classnames from "classnames";
import PropTypes from "prop-types";
import { showSuccessToast } from "~/components/Toast";
import { stopwatchPause, stopwatchResume, stopwatchStart } from "~/store/actions/actions.stopwatches";
import { getStopwatchByParentTypeAndParentId } from "~/store/selectors/selector.stopwatches";
import { stopwatchMaximum } from "~/utils/date";
import PlayPauseButton from "./PlayPauseButton";

const StopwatchSwitch = ({ parentType, parentId, className }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isPlaying, setIsPlaying] = useState(false);

  const rawStopwatch = useSelector((state) => getStopwatchByParentTypeAndParentId(state, parentType, parentId));

  const stopwatch = useMemo(
    () => ({
      id: rawStopwatch?.id || 0,
      started_on: rawStopwatch?.started_on || 0,
      elapsed: rawStopwatch?.elapsed || 0,
    }),
    [rawStopwatch?.elapsed, rawStopwatch?.id, rawStopwatch?.started_on]
  );

  useEffect(() => {
    if (stopwatch.id > 0 && stopwatch.started_on > 0) {
      return setIsPlaying(true);
    }
    return setIsPlaying(false);
  }, [stopwatch]);

  const label = useMemo(() => {
    if (isPlaying) {
      return "Pause Tracking";
    }
    if (stopwatch.id > 0) {
      return "Continue Tracking";
    }
    return "Start Tracking";
  }, [isPlaying, stopwatch]);

  const handleClickPlayButton = useCallback(
    (e) => {
      if (e) {
        e.stopPropagation();
      }
      if (stopwatch.elapsed >= stopwatchMaximum) {
        return showSuccessToast(t("Stopwatch for this task has reached the time limit (99:59)"));
      }
      stopwatch.id > 0
        ? dispatch(stopwatchResume(stopwatch.id, stopwatch.started_on))
        : dispatch(stopwatchStart(parentType, parentId));
    },
    [dispatch, parentId, parentType, stopwatch.elapsed, stopwatch.id, stopwatch.started_on, t]
  );

  const handleClickPauseButton = useCallback(
    (e) => {
      if (e) {
        e.stopPropagation();
      }
      dispatch(stopwatchPause(stopwatch.id, stopwatch.started_on, stopwatch.elapsed, 0));
    },
    [dispatch, stopwatch.elapsed, stopwatch.id, stopwatch.started_on]
  );

  return (
    <Tooltip title={label}>
      <div
        data-testid="stopwatch-switch"
        className={classnames("tw-w-6 tw-h-6 tw-cursor-pointer tw-relative stopwatch_switch_wrapper", className)}
      >
        <PlayPauseButton
          isPlaying={isPlaying}
          onPlay={handleClickPlayButton}
          onPause={handleClickPauseButton}
        />
        <IconButton
          className={classnames("stopwatch_switch_icon tw-absolute", {
            stopwatch_switch_icon_playing: isPlaying,
            "tw-opacity-25": !stopwatch.id,
          })}
          size="small"
          variant="text gray"
        >
          <ClockStopwatchIcon className="tw-h-6 tw-w-6" />
        </IconButton>
      </div>
    </Tooltip>
  );
};

StopwatchSwitch.propTypes = {
  parentType: PropTypes.string,
  parentId: PropTypes.number,
  className: PropTypes.string,
};

StopwatchSwitch.displayName = "StopwatchSwitch";

export default StopwatchSwitch;
